import { useLocation, Link } from "react-router-dom"
import { HugeiconsIcon } from "@hugeicons/react"
import { ChevronRight, LogOut } from "@hugeicons/core-free-icons"
import { useAuth } from "@/lib/auth/use-auth"
import { useLogout } from "@/lib/auth/auth-queries"
import { useFeatureFlags } from "@/features/feature-flags/queries/feature-flags-queries"
import { omsLinks, type NavLink } from "./nav-links"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

const SERVER_URL = import.meta.env.VITE_API_BASE_URL || ""

function isLinkVisible(
  link: NavLink,
  userRole: string | null,
  flags: Record<string, boolean>,
): boolean {
  if (link.featureFlag && !flags[link.featureFlag]) return false
  if (!link.roles) return true
  if (!userRole) return false
  return link.roles.includes(userRole as never)
}

function NavItem({ link }: { link: NavLink }) {
  const location = useLocation()
  const { role } = useAuth()
  const { data: flagsResponse } = useFeatureFlags()
  const flags = flagsResponse?.flags ?? {}
  const { isMobile, state, setOpenMobile } = useSidebar()
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false)
  }

  if (link.subLinks && link.subLinks.length > 0) {
    const visibleSubLinks = link.subLinks.filter((sub) =>
      isLinkVisible(sub, role, flags)
    )
    if (visibleSubLinks.length === 0) return null

    const isAnySubActive = visibleSubLinks.some(
      (sub) => sub.to && location.pathname === sub.to
    )

    if (state === "collapsed" && !isMobile) {
      return (
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <SidebarMenuButton
                  tooltip={link.text}
                  isActive={isAnySubActive}
                >
                  <HugeiconsIcon icon={link.icon} size={18} />
                  <span>{link.text}</span>
                </SidebarMenuButton>
              }
            />
            <DropdownMenuContent
              side="right"
              align="start"
              sideOffset={8}
              className="w-56 min-w-56"
            >
              {visibleSubLinks.map((sub) => (
                <DropdownMenuItem
                  key={sub.text}
                  className={
                    sub.to === location.pathname
                      ? "bg-accent text-accent-foreground"
                      : undefined
                  }
                  render={
                    sub.externalUrl ? (
                      <a
                        href={`${SERVER_URL}/${sub.externalUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    ) : (
                      <Link to={sub.to!} onClick={closeOnMobile} />
                    )
                  }
                >
                  <HugeiconsIcon icon={sub.icon} size={16} />
                  <span>{sub.text}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      )
    }

    return (
      <Collapsible defaultOpen={isAnySubActive} className="group/collapsible">
        <SidebarMenuItem>
          <CollapsibleTrigger
            render={
              <SidebarMenuButton tooltip={link.text} className="cursor-pointer">
                <HugeiconsIcon icon={link.icon} size={18} />
                <span>{link.text}</span>
                <HugeiconsIcon
                  icon={ChevronRight}
                  size={16}
                  className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                />
              </SidebarMenuButton>
            }
          />
          <CollapsibleContent>
            <SidebarMenuSub>
              {visibleSubLinks.map((sub) => (
                <SidebarMenuSubItem key={sub.text}>
                  {sub.externalUrl ? (
                    <SidebarMenuSubButton
                      render={
                        <a
                          href={`${SERVER_URL}/${sub.externalUrl}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        />
                      }
                    >
                      <HugeiconsIcon icon={sub.icon} size={16} />
                      <span>{sub.text}</span>
                    </SidebarMenuSubButton>
                  ) : (
                    <SidebarMenuSubButton
                      render={<Link to={sub.to!} onClick={closeOnMobile} />}
                      isActive={sub.to === location.pathname}
                    >
                      <HugeiconsIcon icon={sub.icon} size={16} />
                      <span>{sub.text}</span>
                    </SidebarMenuSubButton>
                  )}
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    )
  }

  if (link.externalUrl) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={link.text}
          render={
            <a
              href={`${SERVER_URL}/${link.externalUrl}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          }
        >
          <HugeiconsIcon icon={link.icon} size={18} />
          <span>{link.text}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<Link to={link.to!} onClick={closeOnMobile} />}
        isActive={link.to === location.pathname}
        tooltip={link.text}
      >
        <HugeiconsIcon icon={link.icon} size={18} />
        <span>{link.text}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

export function AppSidebar() {
  const { user, role, fullName, initials } = useAuth()
  const logoutMutation = useLogout()
  const { data: flagsResponse } = useFeatureFlags()
  const flags = flagsResponse?.flags ?? {}
  const { isMobile, setOpenMobile } = useSidebar()
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false)
  }

  const visibleLinks = omsLinks.filter((link) => isLinkVisible(link, role, flags))

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-12 justify-center border-b border-sidebar-border px-2 py-0">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<Link to="/" onClick={closeOnMobile} />}>
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                  {initials || "HC"}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {fullName || "Homewood Creations"}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {user?.email || ""}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleLinks.map((link) => (
                <NavItem key={link.text} link={link} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Logout"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              <HugeiconsIcon icon={LogOut} size={18} />
              <span>
                {logoutMutation.isPending ? "Logging out..." : "Logout"}
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
